// ─────────────────────────────────────────────────────────────────────────────
// Manual Trading order-result classifier (PAPER + LIVE).
//
// PURE module (no imports) so it is unit-testable in isolation. It NEVER infers a fill from HTTP 200,
// dispatch success, broker order id, LTP, or requested price. It classifies strictly from the backend
// response fields (mode / status / confirmation_status / dispatch_status) and never interpolates
// null/undefined into user-facing text.
//
// Returned shape: { level: 'success'|'warning'|'error', mode: 'PAPER'|'LIVE'|null, text: string,
//                   terminalRejected: bool, uncertain: bool, paperFill: bool }
// ─────────────────────────────────────────────────────────────────────────────

const up = (v) => String(v ?? '').trim().toUpperCase();

// Normalize a mode value (response.mode or a deployment execution mode) to PAPER | LIVE | null.
export function normalizeMode(v) {
  const u = up(v);
  return u === 'PAPER' || u === 'LIVE' ? u : null;
}

// Safe backend error reason for HTTP 4xx/5xx (never expose raw payloads/objects/tokens).
export function extractOrderErrorMessage(e) {
  const detail = e?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (typeof e?.message === 'string' && e.message.trim()) return e.message;
  return 'Order request failed';
}

// Build a string from parts, dropping any null/empty/undefined segment.
const join = (parts) => parts.filter((p) => p != null && p !== '').join('');

const UNCERTAIN_TEXT = (verb, sym) =>
  `LIVE ${verb} status uncertain — ${sym} — The broker response is unresolved. Check Orders before submitting again.`;

// LIVE entry (BUY/SELL) statuses (see backend manual_trading_signal._manual_live_status).
const LIVE_REJECT_STATUS = new Set(['REJECTED', 'FAILED_BEFORE_DISPATCH']);
const LIVE_SUBMIT_STATUS = new Set(['ACCEPTED', 'SUBMITTED', 'AWAITING_CONFIRMATION']);

export function classifyManualSignalResult(res, { fallbackMode, side } = {}) {
  const mode = normalizeMode(res?.mode) || normalizeMode(fallbackMode) || null;
  const verb = up(side) === 'SELL' ? 'SELL' : 'BUY';
  const sym = res?.executable_symbol || res?.underlying_symbol || '';

  if (mode === 'PAPER') {
    const text = join([
      `PAPER ${verb} placed — ${sym}`,
      res?.quantity != null ? ` ×${res.quantity}` : '',
      res?.fill_price != null && res.fill_price !== '' ? ` @ ${res.fill_price}` : '',
      res?.paper_order_id ? ` (order ${res.paper_order_id})` : '',
    ]);
    return { level: 'success', mode, text, terminalRejected: false, uncertain: false, paperFill: true };
  }

  if (mode === 'LIVE') {
    const cs = up(res?.confirmation_status);
    const st = up(res?.status);
    const ds = up(res?.dispatch_status);

    // 1) Definitive failure / rejection.
    if (cs === 'CONFIRMED_REJECT' || LIVE_REJECT_STATUS.has(st) || ds === 'REJECTED' || ds === 'FAILED') {
      return {
        level: 'error', mode,
        text: `LIVE ${verb} rejected — ${sym} — Broker rejected the order`,
        terminalRejected: true, uncertain: false, paperFill: false,
      };
    }
    // 2) Uncertain / unresolved.
    if (cs === 'TIMEOUT' || st === 'UNKNOWN' || ds === 'UNKNOWN') {
      return { level: 'warning', mode, text: UNCERTAIN_TEXT(verb, sym), terminalRejected: false, uncertain: true, paperFill: false };
    }
    // 3) Confirmed fill (the ONLY "filled" classification). Price shown only if explicitly present.
    if (cs === 'CONFIRMED_FILL') {
      const text = join([
        `LIVE ${verb} filled — ${sym}`,
        res?.quantity != null ? ` ×${res.quantity}` : '',
        res?.fill_price != null && res.fill_price !== '' ? ` @ ${res.fill_price}` : '',
      ]);
      return { level: 'success', mode, text, terminalRejected: false, uncertain: false, paperFill: false };
    }
    // 4) Submitted / accepted — explicit allowlist only (no fill price, no overlay implied).
    if (cs === 'AWAITING' || LIVE_SUBMIT_STATUS.has(st)) {
      const broker = (res?.broker_name && res?.broker_order_id) ? ` · ${res.broker_name} order ${res.broker_order_id}` : '';
      return {
        level: 'success', mode,
        text: `LIVE ${verb} submitted — ${sym} — Awaiting broker confirmation${broker}`,
        terminalRejected: false, uncertain: false, paperFill: false,
      };
    }
    // 5) NOT_TRACKED / null / unrecognized LIVE combination → uncertain (never blind success).
    return { level: 'warning', mode, text: UNCERTAIN_TEXT(verb, sym), terminalRejected: false, uncertain: true, paperFill: false };
  }

  // Unknown mode — never claim PAPER. Generic, truthful (the request returned 200).
  return {
    level: 'success', mode: null,
    text: `${verb} order submitted — ${sym}`,
    terminalRejected: false, uncertain: false, paperFill: false,
  };
}

// LIVE/PAPER individual-exit row statuses (single exit + exit-all row union).
const EXIT_REJECT = new Set(['EXIT_REJECTED', 'RECONCILIATION_MISMATCH']);
const EXIT_SUBMIT = new Set(['EXIT_SUBMITTED', 'EXIT_AWAITING_CONFIRMATION', 'ACCEPTED', 'SUBMITTED']);
const EXIT_FLAT = new Set(['ALREADY_FLAT', 'BROKER_FLAT_RECONCILED']);

export function classifyManualExitResult(res, { fallbackMode } = {}) {
  const mode = normalizeMode(res?.mode) || normalizeMode(fallbackMode) || null;
  const sym = res?.executable_symbol || res?.underlying_symbol || '';
  const cs = up(res?.confirmation_status);
  const st = up(res?.status);
  const ds = up(res?.dispatch_status);
  const detail = typeof res?.error_detail === 'string' && res.error_detail.trim() ? res.error_detail : '';

  if (mode === 'PAPER') {
    // PAPER exit is a synchronous local fill (existing behavior), null-guarded.
    const text = join([
      `PAPER position exited — ${sym}`,
      res?.quantity != null ? ` ×${res.quantity}` : '',
      res?.fill_price != null && res.fill_price !== '' ? ` @ ${res.fill_price}` : '',
    ]);
    return { level: 'success', mode, text, terminalRejected: false, uncertain: false };
  }

  // LIVE (or unknown-mode exit) — explicit mapping; broker-flat is NOT broker-filled.
  if (cs === 'CONFIRMED_REJECT' || EXIT_REJECT.has(st) || ds === 'REJECTED' || ds === 'FAILED') {
    return { level: 'error', mode, text: join([`LIVE exit rejected — ${sym}`, detail ? ` — ${detail}` : '']), terminalRejected: true, uncertain: false };
  }
  if (st === 'PENDING_ENTRY_REMAINDER') {
    return { level: 'warning', mode, text: `LIVE exit incomplete — ${sym} — Entry remainder pending. Check Orders.`, terminalRejected: false, uncertain: true };
  }
  if (st === 'ALREADY_EXITING') {
    return { level: 'warning', mode, text: `Exit already in progress — ${sym}`, terminalRejected: false, uncertain: true };
  }
  if (EXIT_FLAT.has(st)) {
    return { level: 'success', mode, text: `Position already flat — ${sym}`, terminalRejected: false, uncertain: false };
  }
  if (cs === 'TIMEOUT' || st === 'UNKNOWN' || ds === 'UNKNOWN') {
    return { level: 'warning', mode, text: `LIVE exit status uncertain — ${sym} — The broker response is unresolved. Check Orders before submitting again.`, terminalRejected: false, uncertain: true };
  }
  if (cs === 'CONFIRMED_FILL') {
    const text = join([`LIVE exit filled — ${sym}`, res?.fill_price != null && res.fill_price !== '' ? ` @ ${res.fill_price}` : '']);
    return { level: 'success', mode, text, terminalRejected: false, uncertain: false };
  }
  if (cs === 'AWAITING' || EXIT_SUBMIT.has(st)) {
    return { level: 'success', mode, text: `LIVE exit submitted — ${sym} — Awaiting broker confirmation`, terminalRejected: false, uncertain: false };
  }
  return { level: 'warning', mode, text: `LIVE exit status uncertain — ${sym} — The broker response is unresolved. Check Orders before submitting again.`, terminalRejected: false, uncertain: true };
}

// Manual LIVE order CANCEL result (see backend ManualTradingCancelConfirmResponse.status). Classifies
// strictly from response.status; prefers the backend `message`. `info` maps to a neutral toast.
// Returned shape: { level: 'success'|'info'|'warning'|'error', text, offerExit, status }
export function classifyManualCancelResult(res) {
  const st = up(res?.status);
  const sym = res?.symbol || '';
  const msg = typeof res?.message === 'string' && res.message.trim() ? res.message : '';
  const offerExit = res?.offer_exit === true && Number(res?.filled_quantity) > 0 && res?.watchlist_row_id != null;
  if (st === 'CANCELLED' || st === 'CANCEL_SUBMITTED') {
    const text = msg || `Order cancel ${st === 'CANCELLED' ? 'completed' : 'submitted'} — ${sym}`;
    return { level: 'success', text, offerExit, status: st };
  }
  if (st === 'ALREADY_CANCELLED') return { level: 'info', text: msg || `Order already cancelled — ${sym}`, offerExit, status: st };
  if (st === 'ALREADY_FILLED') return { level: 'info', text: msg || `Order already filled — ${sym}`, offerExit, status: st };
  if (st === 'NOT_CANCELABLE') return { level: 'warning', text: msg || `Order is not cancelable — ${sym}`, offerExit, status: st };
  if (st === 'BROKER_STATUS_UNAVAILABLE') return { level: 'warning', text: msg || `Broker order status unavailable — cancel not sent — ${sym}`, offerExit: false, status: st };
  if (st === 'CANCEL_REJECTED') return { level: 'error', text: msg || `Cancel rejected — ${sym}`, offerExit, status: st };
  return { level: 'warning', text: msg || `Cancel status uncertain — ${sym}`, offerExit, status: st };
}
